import {
  createContext,
  useContext,
  useMemo,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { apiClient, setTokenGetter } from '@/api/axios';

// Type for Clerk user from useUser hook
type ClerkUser = ReturnType<typeof useUser>['user'];

export type UserType = 'petso' | 'petso-pro' | 'farmso';

export interface User {
  email: string;
  name: string;
  imageUrl?: string;
  authMethod: string;
  walletAddress?: string;
  role?: string;
  type: UserType;
  clerkId: string;
  // For petso-pro
  businessName?: string;
  specialization?: string;
  location?: string;
  verified?: boolean;
  rating?: number;
  totalReviews?: number;
}

interface SyncUserResponse {
  message: string;
  user: {
    clerkId: string;
    email: string;
    name: string;
    userType: UserType;
    walletAddress?: string;
  };
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  clerkUser: ClerkUser;
  isLoading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Helper function to map Clerk user to our User type
function mapClerkUserToUser(
  clerkUser: ClerkUser,
  userType: UserType = 'petso',
  walletAddressOverride?: string
): User | null {
  if (!clerkUser) return null;

  const primaryEmail = clerkUser.primaryEmailAddress?.emailAddress || '';
  const fullName =
    clerkUser.fullName ||
    `${clerkUser.firstName || ''} ${clerkUser.lastName || ''}`.trim() ||
    primaryEmail;

  // Get user type from public metadata or default to 'petso'
  const metadata = clerkUser.publicMetadata as Record<string, unknown>;
  const type = (metadata?.userType as UserType) || userType;

  return {
    email: primaryEmail,
    name: fullName,
    imageUrl: clerkUser.imageUrl || undefined,
    authMethod: clerkUser.externalAccounts?.[0]?.provider || 'email',
    walletAddress:
      walletAddressOverride || (metadata?.walletAddress as string | undefined),
    role: metadata?.role as string | undefined,
    type,
    clerkId: clerkUser.id,
    businessName: metadata?.businessName as string | undefined,
    specialization: metadata?.specialization as string | undefined,
    location: metadata?.location as string | undefined,
    verified: metadata?.verified as boolean | undefined,
    rating: metadata?.rating as number | undefined,
    totalReviews: metadata?.totalReviews as number | undefined,
  };
}

let send = false;

export function AuthProvider({ children }: { children: ReactNode }) {
  const { user: clerkUser, isLoaded: isClerkLoaded } = useUser();
  const { isSignedIn, getToken, signOut } = useClerkAuth();
  const hasSyncedRef = useRef<Set<string>>(new Set());
  const isSyncingRef = useRef(false);
  const [apiWalletAddress, setApiWalletAddress] = useState<string | undefined>(
    undefined
  );

  // Logout function using Clerk
  const logout = async () => {
    try {
      await signOut();
      hasSyncedRef.current.clear();
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  // Set up the token getter for axios
  useEffect(() => {
    setTokenGetter(async () => {
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
  }, [getToken]);

  const user = useMemo(() => {
    if (!isClerkLoaded) return null;
    return mapClerkUserToUser(clerkUser, 'petso', apiWalletAddress);
  }, [clerkUser, isClerkLoaded, apiWalletAddress]);

  // Sync user to database after login
  useEffect(() => {
    if (!isClerkLoaded || !isSignedIn || !clerkUser) {
      return;
    }

    const userId = clerkUser.id;

    // Prevent duplicate syncs for the same user session
    if (hasSyncedRef.current.has(userId) || isSyncingRef.current) {
      return;
    }

    // Sync user to database with a small delay to ensure token is ready
    const syncUser = async () => {
      if (send) return;
      send = true;
      isSyncingRef.current = true;

      try {
        // Wait a bit for the session to be fully established
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Verify we still have a valid session
        const token = await getToken();
        if (!token) {
          console.warn('No token available for sync, skipping...');
          isSyncingRef.current = false;
          return;
        }

        const metadata = clerkUser.publicMetadata as Record<string, unknown>;

        // Pick up referral code stored by /join route (if the user arrived via a referral link)
        let storedReferralCode: string | null = null;
        try {
          storedReferralCode = sessionStorage.getItem('petso_referral_code');
        } catch {
          // sessionStorage may be unavailable
        }

        const response = await apiClient.post<SyncUserResponse>('/users/sync', {
          userType: metadata?.userType || 'petso',
          walletAddress: metadata?.walletAddress,
          businessName: metadata?.businessName,
          specialization: metadata?.specialization,
          location: metadata?.location,
          ...(storedReferralCode ? { referralCode: storedReferralCode } : {}),
        });

        // Save wallet address from API response
        if (response.data?.user?.walletAddress) {
          setApiWalletAddress(response.data.user.walletAddress);
        }

        // If this was a new user created via referral link, apply the referral
        // so a pending referral record is created for the referrer.
        if (storedReferralCode && response.status === 201) {
          try {
            await apiClient.post('/referrals/apply', { referralCode: storedReferralCode });
          } catch {
            // Referral may already be applied or code invalid — non-blocking
          }
          try {
            sessionStorage.removeItem('petso_referral_code');
          } catch {
            // ignore
          }
        }

        hasSyncedRef.current.add(userId);
        console.log(
          'User synced to database successfully',
          response.data?.user?.walletAddress
        );
      } catch (error) {
        console.error('Failed to sync user to database:', error);
        // Don't block the user experience if sync fails
        // The sync can be retried on next login
      } finally {
        isSyncingRef.current = false;
      }
    };

    syncUser();
  }, [isClerkLoaded, isSignedIn, clerkUser, getToken]);

  // Clear sync tracking when user logs out
  useEffect(() => {
    if (!isSignedIn) {
      hasSyncedRef.current.clear();
    }
  }, [isSignedIn]);

  const value: AuthContextType = {
    user,
    isAuthenticated: isSignedIn ?? false,
    clerkUser,
    isLoading: !isClerkLoaded,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
